import { inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { Store } from '@ngrx/store';
import { asapScheduler, BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, observeOn, switchMap, take, takeUntil } from 'rxjs/operators';
import { TabsState } from '../../store/tab/tab.reducer';
import * as TabActions from '../../store/tab/tab.actions';
import * as TabSelectors from '../../store/tab/tab.selectors';
import { BasePage } from '../../pages/base/base.page';
import { Tab } from '../../services/tab/tab.service';

@Injectable()
export abstract class Tabbing<T> extends BasePage implements OnDestroy {
  isActiveTab$: Observable<boolean>;

  private _isActive = false;
  private _pendingDeactivation = false;
  protected deactivated$ = new Subject<void>();

  constructor(protected tabStore$: Store<TabsState>) {
    super();

    const activatedRoute = inject(ActivatedRoute);

    let initialTabId: string;
    this.tabStore$
      .select(TabSelectors.getActiveTabId)
      .pipe(take(1))
      .subscribe(id => (initialTabId = id));

    const tabId$ = new BehaviorSubject<string>(initialTabId);

    activatedRoute.params
      .pipe(
        switchMap(() => this.tabStore$.select(TabSelectors.getActiveTabId).pipe(take(1))),
        takeUntil(this.destroyed$)
      )
      .subscribe(id => tabId$.next(id));

    this.isActiveTab$ = combineLatest([this.tabStore$.select(TabSelectors.getActiveTabId), tabId$]).pipe(
      map(([activeTabId, tabId]) => activeTabId === tabId),
      distinctUntilChanged()
    );

    // Syncs update deactivation state; asapScheduler subscriptoins below handles the actual lifecycle hooks
    // sets _pendingDeactivation and fires deactivated$ so state is settled before onTabDeactivated() runs
    this.isActiveTab$.pipe(takeUntil(this.destroyed$)).subscribe(isActive => {
      if (this._isActive && !isActive) {
        this._pendingDeactivation = true;
        this.deactivated$.next();
      } else if (isActive) {
        this._pendingDeactivation = false;
      }
      this._isActive = isActive;
    });

    this.isActiveTab$.pipe(observeOn(asapScheduler), takeUntil(this.destroyed$)).subscribe(isActive => {
      if (isActive) {
        this.onTabActivated();
      } else {
        this._pendingDeactivation = false;
        this.onTabDeactivated();
      }
    });

    this.validateTabbingConfig();
  }

  abstract getTabbingConfig(): Tab<T>;

  protected onTabActivated(): void {}

  protected onTabDeactivated(): void {}

  ngOnDestroy(): void {
    // If destroyed while still active, manually trigger deactivation lifecycle.
    // If destroyed after going inactive but before asapScheduler fires onTabDeactivated(),
    if (this._isActive) {
      this.deactivated$.next();
      this.onTabDeactivated();
    } else if (this._pendingDeactivation) {
      this.onTabDeactivated();
    }
    this.deactivated$.complete();
    super.ngOnDestroy();
  }

  /**
   *
   * @param name - name of the tab
   * @returns Action to be dispatched
   * Dispatches Tab Action to update the tab name
   */
  public updateTabName(name: string): void {
    this.isActiveTab$.pipe(take(1)).subscribe(isActive => {
      if (isActive) {
        this.tabStore$.dispatch(TabActions.updateTabName({ name }));
      }
    });
  }

  public navigate(route: string[], navigationExtras?: NavigationExtras): void {
    this.tabStore$.dispatch(TabActions.navigate({ route, navigationExtras }));
  }

  public navigateAssumeCurrentTab(route, navigationExtras?: NavigationExtras): void {
    this.tabStore$.dispatch(TabActions.navigateAssumeCurrentTab({ route, navigationExtras }));
  }

  private validateTabbingConfig(): void {
    const config = this.getTabbingConfig();

    if (
      !config.selectorState &&
      (config.closeWarningSelector ||
        config.initializeActionOnCacheLoaded ||
        config.initializeAction ||
        config.activatedAction ||
        config.restoreSnapshotAction ||
        config.rehydrateStateAction ||
        config.noSnapshotAction ||
        config.closeTabAction)
    ) {
      console.error(`${config.route} TabbingConfig Error: "selectorState" must be set if using Tab evnets.`);
    }
    if (config.initializeActionOnCacheLoaded && !config.initializeAction) {
      console.error(`${config.route} TabbingConfig Error: "initializeActionOnCacheLoaded" is set but there is no "initializeAction".`);
    }
    if ((config.ttl && !config.rehydrateStateAction) || (!config.ttl && config.rehydrateStateAction)) {
      console.error(`${config.route} TabbingConfig Error: "ttl" and "rehydrateStateAction" need to both be set.`);
    }
    if (config.closeWarningSelector && config.closeWarning) {
      console.error(`${config.route} TabbingConfig Error: "closeWarningSelector" and "closeWarning" shouldn't be set at the same time.`);
    }
    if (config.matIcon && config.svgIcon) {
      console.error(`${config.route} TabbingConfig Error: "matIcon" and "svgIcon" shouldn't be set at the same time.`);
    }
  }
}
